import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { CONTACT, OPERATOR } from "../../../components/PublicLayout";
import type { TextGuide } from "../../../api/textGuide";
import { FAQ } from "../textGuideParts";
import { STAGES, type JourneyCommand } from "./journeyData";

function Accordion({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <details className="guide-faq group border-[3px] border-[#3A3F48] bg-[#1F232A] open:border-[#FFFFFF]" data-testid={testId}>
      <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 font-condensed text-lg font-extrabold uppercase tracking-[0.04em] text-[#FFFFFF] [&::-webkit-details-marker]:hidden">
        {title}<ChevronDown className="h-5 w-5 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden />
      </summary>
      <div className="space-y-2 border-t-[3px] border-[#3A3F48] px-4 py-4 text-[15px] text-[#FFFFFF]">{children}</div>
    </details>
  );
}

function H2({ id, children }: { id: string; children: ReactNode }) {
  return <h2 id={id} className="scroll-mt-32 font-condensed text-3xl font-extrabold uppercase leading-none text-[#FFFFFF] sm:text-4xl">{children}</h2>;
}

export function GuideSections({ g }: { g: TextGuide }) {
  useEffect(() => {
    const open = () => document.querySelectorAll<HTMLDetailsElement>(".guide-faq").forEach((d) => { d.open = true; });
    window.addEventListener("beforeprint", open);
    return () => window.removeEventListener("beforeprint", open);
  }, []);
  return (
    <div className="guide-body mt-16 space-y-12">
      <section className="guide-section space-y-3" data-testid="guide-section-location">
        <H2 id="location">Location <span className="text-[#C92E2B]">link</span></H2>
        <Accordion title="How the link after IN and OUT works" testId="guide-gps-note">
          <ol className="list-decimal space-y-1.5 pl-5">
            <li>We reply with a link. Open it and tap the button. Allow location when your phone asks.</li>
            <li>We check once that you're at the venue, then record the punch. We never track you after that.</li>
            <li>The link works once and expires after {g.punch_link_ttl_min} minutes.</li>
            <li>Expired, or location denied? Turn location on for your browser and text IN (or OUT) again. Still stuck? Text FIX. Your text time is saved either way.</li>
          </ol>
          <p className="text-sm text-[#B8BEC8]">Turning location back on: see <Link to="/help" className="font-semibold text-[#F5D536] underline">Help</Link>.</p>
        </Accordion>
      </section>
      <section className="guide-section space-y-3" data-testid="guide-section-faq">
        <H2 id="faq">FAQ</H2>
        {FAQ.map(([q, a], i) => <Accordion key={q} title={q} testId={`guide-faq-${i}`}><p>{a}</p></Accordion>)}
      </section>
      <section className="guide-section space-y-3" data-testid="guide-section-crew-leads">
        <H2 id="crew-leads">For crew <span className="text-[#C92E2B]">leads</span></H2>
        <Accordion title="Alert texts for LATE and DECLINE" testId="guide-crew-leads-alerts">
          <p>When a crew member texts LATE or DECLINE, every admin with alert texts turned on gets a short text, for example "Jordan S. running late ~15 min for 6 PM Allegiant Stadium". LATE alerts are sent at most once every 15 minutes per shift. The shift also shows "Running late" or "Needs reassignment" in the app.</p>
          <p>To turn them on: sign in, go to <strong>Settings → My security → Crew alert texts</strong>, enter your mobile number, confirm the code we text you, check the consent box and switch alerts on. Reply STOP to that number to turn them off.</p>
        </Accordion>
        <Accordion title="Asking crew to confirm a shift" testId="guide-crew-leads-offers">
          <p>Check <strong>Text crew to confirm</strong> when you create or edit a shift, or when you run a template. Crew reply CONFIRM or DECLINE.</p>
        </Accordion>
      </section>
    </div>
  );
}

export function Compliance() {
  return (
    <section className="guide-compliance mt-16 border-[3px] border-[#3A3F48] p-5 text-sm text-[#FFFFFF]" data-testid="guide-compliance">
      <p className="font-semibold">Msg frequency varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out.</p>
      <p className="mt-2 text-[#B8BEC8]">See our <Link to="/privacy" className="font-semibold text-[#F5D536] underline" data-testid="guide-privacy-link">Privacy Policy</Link> and <Link to="/terms" className="font-semibold text-[#F5D536] underline" data-testid="guide-terms-link">Terms</Link>. Program details: <Link to="/sms" className="font-semibold text-[#F5D536] underline" data-testid="guide-sms-link">SMS Guide</Link>. Questions: <a href={`mailto:${CONTACT}`} className="font-semibold text-[#F5D536] underline">{CONTACT}</a></p>
      <p className="mt-2 text-[#B8BEC8]" data-testid="guide-operator">{OPERATOR}</p>
    </section>
  );
}

/** Print only: the one-page cheat sheet. */
export function PrintSheet({ cmds }: { cmds: JourneyCommand[] }) {
  return (
    <div className="print-only guide-body" aria-hidden>
      {STAGES.map((s) => (
        <section key={s.id} className="guide-section">
          <h2>{s.id} {s.name}</h2>
          {cmds.filter((c) => c.stage.id === s.id).map((c) => (
            <article key={c.code} className="guide-card">
              <h3>{c.code}{c.forms.length > 1 ? ` (${c.forms.slice(1).join(", ")})` : ""}{c.synonyms.length ? ` · also ${c.synonyms.join(", ")}` : ""}</h3>
              <p>{c.does}</p>
              <p><b>When:</b> {c.window}</p>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
